const ORG_PATH_ROOT = '/organization';
const ORG_PATH_PREFIX = `${ORG_PATH_ROOT}/`;

interface OrgPathPrefixInput {
  /** The org selected in the manager machine. */
  orgId: string | undefined;
  /** `org_id` as the matched route reports it. */
  orgIdParam: string | undefined;
  pathname: string;
}

/**
 * Whether an address lies outside `/organization/…`, so that `RouteDataProvider`
 * will move it under the selected org once there is one. The bare
 * `/organization` counts as inside: nothing is done to it.
 */
export const isOutsideOrgPath = (pathname: string): boolean =>
  pathname !== ORG_PATH_ROOT && !pathname.startsWith(ORG_PATH_PREFIX);

/**
 * Whether a path that carries no org should be moved under the selected org
 * (`/my-account` -> `/organization/<id>/my-account`).
 *
 * "The route reports no `org_id`" cannot be the whole test: the not-found route
 * matches any address, including ones already under `/organization/`, and
 * reports no `org_id` for them either. Prefixing those again gave a path that
 * is again unmatched, and so on without end (PSTUDIO-4397). A path that already
 * starts with the prefix is therefore left alone, and so is the bare
 * `/organization`: prefixing it would only move the not-found page to an
 * address that names an organization twice.
 *
 * LIMITATION: this reads the path text, not the route table, so an address under
 * `/organization/` that no route matches is shown the not-found page as it is,
 * rather than being repaired.
 */
export const shouldPrefixPathWithOrg = ({
  orgId,
  orgIdParam,
  pathname,
}: OrgPathPrefixInput): boolean =>
  !!orgId && !orgIdParam && isOutsideOrgPath(pathname);

interface OrgSelectionInput {
  /** `org_id` as the matched route reports it. */
  routeOrgId: string | undefined;
  /** The org selected in the manager machine. */
  selectedOrgId: string | undefined;
  /** The matched route is the not-found route (a splat). */
  isFallbackRoute: boolean;
}

/**
 * Whether the route's org should be sent to the manager machine.
 *
 * A route without `org_id` normally means "no org named, use the default", and
 * the machine does exactly that for `undefined`. The not-found route is the
 * exception: it reports no `org_id` even for `/organization/<id>/typo`, where
 * the person IS inside an org. Sending `undefined` there would swap the org they
 * were working in for their default one (PSTUDIO-4397). It is still sent while
 * nothing is selected, since that is what establishes the default.
 *
 * LIMITATION: the id in such an address is never read, so the address and the
 * selected org can disagree. On a cold load the default org is selected while
 * the address names another; inside the app the org already selected stays,
 * whichever org the address names. Either way the page's action leads to the
 * selected org's home, and the membership check in `RouteDataProvider` does not
 * run, since it needs the route's `org_id`. The org switcher in the navigation
 * does not switch here either: it only navigates (`primary-nav.tsx`,
 * `onSelectionChange`), and the org changes only through this sync, which sees
 * no `org_id` on this route.
 */
export const shouldSelectOrgFromRoute = ({
  routeOrgId,
  selectedOrgId,
  isFallbackRoute,
}: OrgSelectionInput): boolean => {
  if (isFallbackRoute && routeOrgId === undefined) {
    return selectedOrgId === undefined;
  }

  return (
    routeOrgId !== selectedOrgId ||
    (routeOrgId === undefined && selectedOrgId === undefined)
  );
};
